import React, { useState, useCallback, useEffect } from 'react';
import { FileUploadItem } from './FileUploadItem';
import { FileUploadProps, UploadedFile } from './types';

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFiles = 5,
  acceptedTypes = ['image/*', 'application/pdf'],
  disabled = false
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // ファイル形式チェック
  const validateFileType = useCallback((file: File): boolean => {
    return acceptedTypes.some(type => {
      if (type === 'image/*') return file.type.startsWith('image/');
      if (type === 'application/pdf') return file.type === 'application/pdf';
      return file.type === type;
    });
  }, [acceptedTypes]);

  // ファイル選択処理
  const handleFileSelect = useCallback(async (selectedFiles: FileList | File[]) => {
    const fileArray = Array.from(selectedFiles);
    
    // ファイル数制限チェック
    const availableSlots = maxFiles - files.length;
    const filesToAdd = fileArray.slice(0, availableSlots);

    const newFiles: UploadedFile[] = filesToAdd
      .filter(validateFileType)
      .map(file => {
        // 毎回新しいプレビューURL生成（同じファイルでも新しいURLを作成）
        const localPreviewUrl = file.type.startsWith('image/') 
          ? URL.createObjectURL(file) 
          : undefined;

        return {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          localPreviewUrl
        };
      });

    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  }, [files, maxFiles, validateFileType, onFilesChange]);


  // ファイル削除処理
  const removeFile = useCallback((fileId: string) => {
    const fileToRemove = files.find(f => f.id === fileId);
    if (fileToRemove?.localPreviewUrl) {
      URL.revokeObjectURL(fileToRemove.localPreviewUrl);
    }
    
    const updatedFiles = files.filter(f => f.id !== fileId);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  }, [files, onFilesChange]);

  // ドラッグ&ドロップ処理
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [disabled, handleFileSelect]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file.localPreviewUrl) {
          URL.revokeObjectURL(file.localPreviewUrl);
        }
      });
    };
  }, [files]);

  // 更新時の親コンポーネント通知は各操作で直接実行するため削除

  const getInputClassName = () => {
    let className = 'file-upload__input';
    if (isDragOver) className += ' file-upload__input--drag-over';
    if (disabled) className += ' file-upload__input--disabled';
    return className;
  };

  const canAddMoreFiles = files.length < maxFiles;

  return (
    <div className="file-upload">
      {canAddMoreFiles && (
        <div
          className={getInputClassName()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            multiple
            accept={acceptedTypes.join(',')}
            onChange={(e) => {
              if (e.target.files) {
                handleFileSelect(e.target.files);
                // inputをリセットして同じファイルも再選択可能にする
                e.target.value = '';
              }
            }}
            className="file-upload__input-field"
            disabled={disabled}
          />
          <button type="button" className="file-upload__button" disabled={disabled}>
            📎 ファイルを選択またはドラッグ&ドロップ
            <br />
            <small>（最大{maxFiles}個、画像・PDF対応）</small>
          </button>
        </div>
      )}

      {files.length > 0 && (
        <div className="file-upload__list">
          {files.map(file => (
            <FileUploadItem
              key={file.id}
              file={file}
              onRemove={removeFile}
            />
          ))}
        </div>
      )}
    </div>
  );
};